import { Clear } from '@mui/icons-material';
import { Autocomplete, Chip, TextField } from '@mui/material';

import useApiEnvironments from '../../../../common/hooks/useApiEnvironments';
import { EnvironmentModel } from '../../../../api/api';

type EnvironmentIdsInputProps = {
	value?: string[] | null;
	onValueChange: (value: string[] | null) => void;
};

type EnvironmentOption = {
	id: string;
	name: string;
	isUnknown?: boolean;
};

const CLEAR_INDICATOR_SX = {
	width: 24,
	height: 24,
	padding: 0,
	margin: 0,
	color: 'action.disabled',
	'&:hover': {
		color: 'action.active',
		backgroundColor: 'transparent',
	},
};

function toOption(environment: EnvironmentModel): EnvironmentOption {
	return {
		id: environment.id,
		name: environment.name ?? environment.id,
	};
}

export default function EnvironmentIdsInput({ value, onValueChange }: EnvironmentIdsInputProps) {
	const selectedIds = Array.isArray(value) ? value : [];
	const { data: environments = [], isLoading, error } = useApiEnvironments();

	const environmentOptions = environments.map(toOption);
	const selectedOptions = selectedIds.map(
		(id) => environmentOptions.find((environment) => environment.id === id) ?? { id, name: id, isUnknown: true }
	);
	const options = [
		...environmentOptions,
		...selectedOptions.filter((selected) => selected.isUnknown && !environmentOptions.some((environment) => environment.id === selected.id)),
	];

	return (
		<Autocomplete
			multiple
			fullWidth
			options={options}
			value={selectedOptions}
			loading={isLoading}
			loadingText="Loading environments..."
			clearText="Clear"
			clearIcon={<Clear fontSize="small" />}
			slotProps={{
				clearIndicator: {
					sx: CLEAR_INDICATOR_SX,
				},
			}}
			disableCloseOnSelect
			isOptionEqualToValue={(option, selected) => option.id === selected.id}
			getOptionLabel={(option) => option.name}
			onChange={(_, selected) => onValueChange(selected.length > 0 ? selected.map((environment) => environment.id) : null)}
			renderTags={(selected, getTagProps) =>
				selected.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" color={option.isUnknown ? 'warning' : 'default'} />)
			}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					error={!!error}
					helperText={error ? 'Failed to load environments.' : undefined}
				/>
			)}
			sx={{ minWidth: 0 }}
		/>
	);
}
